import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { checkForOverride } from "../helpers/theme";
import { stringToMS } from "../helpers/time";
import { ReplaceWithDataSource } from "../helpers/variable";
import useGetMediaLocation from "../hooks/useGetMediaLocation";
import AnimatedBackground from "./AnimatedBackground";
import Audio from "./Audio";
import LowerThird from "./LowerThird";
import LowerThirdAnimated from "./LowerThirdAnimated";
import OutputCueStyles from "./OutputCueStyles";
import QR from "./QR";
import Ticker from "./Ticker";
import TimeCountdown from "./TimeCountdown";
import BigTimeCountdown from "./BigTimeCountdown";
import BigTimeCountup from "./BigTimeCountup";
import BigTimeToTod from "./BigTimeToTod";
import TimeCountup from "./TimeCountup";
import TimeOfDay from "./TimeOfDay";
import TimeToTimeOfDay from "./TimeToTimeOfDay";
import { cuesApi } from "../../imports";
import NowNextThen from "./NowNextThen";
import Map from "./Map";
import Lyrics from "./Lyrics";
import EmojiReactions from "./EmojiReactions";
import Video from "./Video";
import Checklist from "./Checklist";
import Credits from "./Credits";
import IconWithMessage from "./IconWithMessage";
import ImageWithMessage from "./ImageWithMessage";
import Score from "./Score";
import Social from "./Social";
import UtilityPattern from "./UtilityPattern";
import UtilityTimeOfDay from "./UtilityTimeOfDay";
import UtilityLargeText from "./UtilityLargeText";
import SpeakerTimer from "./UtilitySpeakerTimer";
import Sync from "./UtilitySync";
import Rive from "./Rive";
import SolidColour from "./SolidColour";
import ImageSequence from "./ImageSequence";
import Slideshow from "./Slideshow";
import Image from "./Image";
import Webpage from "./Webpage";
import Message from "./Message";
import InteractionCurrentQuestion from './InteractionCurrentQuestion'
import InteractionJoin from './InteractionJoin'
import CustomHTML from "./CustomHTML";
import Progress from "./Progress";

function useQuery() {
	const { search } = useLocation();

	return useMemo(() => new URLSearchParams(search), [search]);
}

export default function OutputCue({ cue, cloud = false, forceMute = false }) {
	// console.log("OutputCue", cue.id);
	const { projectId } = useParams();

	const isPreview = useLocation().pathname.includes("/preview/");

	const mediaLocation = useGetMediaLocation();

	let query = useQuery();
	const selectedProject = useSelector(
		(state) => state?.main?.projects?.[projectId]
	);

	const themes = useSelector((state) =>
		cloud
			? state?.projects?.[projectId + "_themes"]
			: state?.main?.projects?.[projectId]?.themes || {}
	);
	const baseTheme = useSelector((state) =>
		cloud
			? state?.projects?.[projectId + "_themes"]?.[cue.theme]
			: cue.theme
			? state?.main?.projects?.[projectId]?.themes?.[cue.theme]
			: state?.main?.projects?.[projectId]?.themes?.[selectedProject?.theme]
	);
	const baseThemeId = cue.theme ? cue.theme : selectedProject?.theme;
	const dynamicText = useSelector(
		(state) => state.main?.projects?.[projectId]?.dynamicText
	);
	const dynamicLists = useSelector(
		(state) => state.main?.projects?.[projectId]?.dynamicLists
	);

	useEffect(() => {
		let keyRefName = `key_ref`;
		if (query.get("key") === "true") {
			if (document.getElementsByClassName(keyRefName)[0] === undefined) {
				const style = document.createElement("style");

				style.className = keyRefName;
				style.textContent = `
				/*Key selectors*/

				.credits {
					filter: none!important;
					color: white!important;
					border-color: white!important;
					filter: brightness(0) invert(1) contrast(100%)!important;
				}

				.key_me, .lower_third, .ticker, .message, .time, .time_tod, .time_to_tod, .time_countdown, .time_countup, .social, .webpage, .score, .now_next_then, .video, .checklist, .lyrics, .map, .qr {
					color: white!important;
					border-color: white!important;
					filter: brightness(0) invert(1) contrast(100%)!important;
				}

				.profile_image, .social, .score, .money-svg, .balloon-svg {
					background: white!important;
					filter: brightness(0) invert(1) contrast(100%)!important;
				}

				.webpage_key {
					filter: brightness(0) invert(1) contrast(100%)!important;
				}

				.image_key {
					filter: brightness(0) invert(1) contrast(100%)!important;

				}

				.big_time_text_key, .big_time_to_tod_text_key, .big_time_countdown_text_key, .big_time_countup_text_key {
					color: white!important;
				}

				.big_time_line_key, .big_time_to_tod_line_key, .big_time_countdown_line_key, .big_time_countup_line_key  {
					color: white!important;
					stroke: white!important;
				}

				.lower_third_animated {
					border-color: white!important;
				}
				`;
				document.head.append(style);
			} else {
			}
		}

		Object.entries(themes)?.map(([id, theme]) => {
			let refName = `theme_${id}_ref`;

			if (document.getElementsByClassName(refName)[0] === undefined) {
				const style = document.createElement("style");

				style.className = refName;
				style.textContent = theme.overrides?.css;
				document.head.append(style);
			} else {
				const style = document.getElementsByClassName(refName)[0];

				style.textContent = theme.overrides?.css;
			}
		});
	}, [themes]);

	switch (cue.type) {
		case "lower_third":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<LowerThird
						cue={cue}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "message":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Message cue={cue} baseTheme={baseTheme} baseThemeId={baseThemeId} />
				</OutputCueStyles>
			);

		case "credits":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Credits
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "now_next_then":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<NowNextThen cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "slideshow":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Slideshow cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "map":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Map cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "interaction_join":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<InteractionJoin cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "interaction_current_question":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<InteractionCurrentQuestion cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "utility_pattern":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<UtilityPattern cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "utility_time_of_day":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<UtilityTimeOfDay cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "utility_large_text":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<UtilityLargeText cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "utility_speaker_timer":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<SpeakerTimer cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "utility_sync":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Sync cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "lyrics":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Lyrics cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "icon_with_message":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<IconWithMessage
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						mediaLocation={mediaLocation}
						isPreview={isPreview}
					/>
				</OutputCueStyles>
			);
		case "image_with_message":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<ImageWithMessage
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						mediaLocation={mediaLocation}
					/>
				</OutputCueStyles>
			);

		case "lower_third_animated":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<LowerThirdAnimated
						cue={cue}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						isPreview={isPreview}
					/>
				</OutputCueStyles>
			);

		case "custom_html":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<CustomHTML
						cue={cue}
					/>
				</OutputCueStyles>
			);

		case "progress":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Progress
						cue={cue}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "time":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					{cue.pre !== "" && (
						<span
							style={{
								color: checkForOverride(
									baseTheme,
									"time",
									cue.preset,
									"h2",
									"colour"
								),
								fontWeight:
									checkForOverride(
										baseTheme,
										"time",
										cue.preset,
										"h2",
										"bold"
									) === true
										? "bold"
										: "normal",
								fontStyle:
									checkForOverride(
										baseTheme,
										"time",
										cue.preset,
										"h2",
										"italic"
									) === true
										? "italic"
										: "normal",
								textDecoration:
									checkForOverride(
										baseTheme,
										"time",
										cue.preset,
										"h2",
										"underline"
									) === true
										? "underline"
										: "none",
							}}
							className={`${baseThemeId}_${
								cue.type
							}_small_text ${baseThemeId}_small_text small_text 
					text-${checkForOverride(baseTheme, "time", cue.preset, "h2", "size")}
					leading-${checkForOverride(baseTheme, "time", cue.preset, "h2", "leading")}
                    `}
						>
							<ReplaceWithDataSource text={cue.pre} styled={true} />
						</span>
					)}
					<span
						style={{
							color: checkForOverride(
								baseTheme,
								"time",
								cue.preset,
								"h1",
								"colour"
							),
							fontWeight:
								checkForOverride(
									baseTheme,
									"time",
									cue.preset,
									"h1",
									"bold"
								) === true
									? "bold"
									: "normal",
							fontStyle:
								checkForOverride(
									baseTheme,
									"time",
									cue.preset,
									"h1",
									"italic"
								) === true
									? "italic"
									: "normal",
							textDecoration:
								checkForOverride(
									baseTheme,
									"time",
									cue.preset,
									"h1",
									"underline"
								) === true
									? "underline"
									: "none",
						}}
						className={`${baseThemeId}_${
							cue.type
						}_large_text ${baseThemeId}_large_text large_text 
					text-${checkForOverride(baseTheme, "time", cue.preset, "h1", "size")}
					leading-${checkForOverride(baseTheme, "time", cue.preset, "h1", "leading")}
                    `}
					>
						<Time cue={cue} cloud={cloud} />
					</span>
				</OutputCueStyles>
			);

		case "time_tod":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<TimeOfDay
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "time_countdown":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<TimeCountdown
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "big_time_countdown":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<BigTimeCountdown
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "big_time_countup":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<BigTimeCountup
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);
		case "big_time_to_tod":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<BigTimeToTod
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "time_to_tod":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<TimeToTimeOfDay
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "time_countup":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<TimeCountup
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "animated_background":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<div
						className={`image_key flex
									w-screen
									h-screen
								`}
					>
						<AnimatedBackground
							cue={cue}
							colour1={cue.colour1}
							colour2={cue.colour2}
						/>
					</div>
				</OutputCueStyles>
			);

		case "big_time":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<div
						className={`flex
							w-screen
							h-screen
							
						`}
					>
						<BigTime cue={cue} cloud={cloud} />
					</div>
				</OutputCueStyles>
			);

		case "image":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Image cue={cue} />
				</OutputCueStyles>
			);

		case "image_sequence":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<ImageSequence cue={cue} cloud={cloud} />
				</OutputCueStyles>
			);

		case "webpage":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Webpage cue={cue} />
				</OutputCueStyles>
			);

		case "video":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Video cue={cue} forceMute={isPreview || forceMute} />
				</OutputCueStyles>
			);

		case "audio":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Audio cue={cue} cloud={cloud} forceMute={isPreview || forceMute} />
				</OutputCueStyles>
			);

		case "ticker":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Ticker cue={cue} baseTheme={baseTheme} baseThemeId={baseThemeId} />
				</OutputCueStyles>
			);

		case "checklist":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Checklist
						cue={cue}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "solid_colour":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<SolidColour cue={cue} />
				</OutputCueStyles>
			);

		case "social":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Social
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						mediaLocation={mediaLocation}
						dynamicLists={dynamicLists}
						dynamicText={dynamicText}
					/>
				</OutputCueStyles>
			);

		case "celebration":
			return <OutputCueStyles cue={cue} cloud={cloud}></OutputCueStyles>;

		case "qr":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<QR
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
					/>
				</OutputCueStyles>
			);

		case "pdf":
			return null;

		case "emoji_reaction":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<EmojiReactions cue={cue} />
				</OutputCueStyles>
			);

		case "score":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Score
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						mediaLocation={mediaLocation}
					/>
				</OutputCueStyles>
			);

		case "rive":
			return (
				<OutputCueStyles cue={cue} cloud={cloud}>
					<Rive
						cue={cue}
						cloud={cloud}
						baseTheme={baseTheme}
						baseThemeId={baseThemeId}
						mediaLocation={mediaLocation}
					/>
				</OutputCueStyles>
			);

		default:
			return null;
	}
}

const TimeContents = ({ cue, cloud, skip }) => {
	let { projectId } = useParams();
	// const [t, setT] = useState();
	const [tN, setTN] = useState();
	const [i, setI] = useState(null);

	const to_time_of_day_diff = (cue) => {
		let now = moment().format("x");
		return cue.endTimestamp - now;
	};
	const countdown_diff = (cue) => {
		let now = moment().format("x");
		return cue.durationMS - (now - cue.timestamp);
	};
	const countup_diff = (cue) => {
		let now = moment().format("x");
		let start = cue.startTimeMS || 0;
		return now - cue.timestamp + start;
	};
	

	useEffect(() => {
		if (cue.status === "coming") {
			if (cue.timerType === "to_time_of_day") {
				let diff = to_time_of_day_diff(cue);
				setTN(diff);
			} else if (cue.timerType === "countdown") {
				let diff = countdown_diff(cue);
				setTN(diff);
			} else if (cue.timerType === "countup") {
				let diff = countup_diff(cue);
				setTN(diff);
			}
		}
		if (cue.status === "onair" || cue.status === "cuedoff") {
			clearInterval(i);
			setI(
				setInterval(() => {
					if (cue.timerType === "to_time_of_day") {
						let diff = to_time_of_day_diff(cue);

						setTN(diff);

						if (cue.timeLeft - diff > 1000 || cue.timeLeft - diff < -1000) {
							cuesApi.updateCue(cue.id, projectId, {
								timeLeft: diff,
							});
						}

						if (diff < 200) {
							clearInterval(i);
							cuesApi.updateCue(cue.id, projectId, {
								status: "going",
							});
						}
					} else if (cue.timerType === "countdown") {
						let diff = countdown_diff(cue);

						if (cue.paused) {
							let paused = cue.durationMS - (cue.pausedAtMS - cue.timestamp);

							setTN(paused);
						} else {
							setTN(diff);

							if (cue.timeLeft - diff > 1000) {
								cuesApi.updateCue(cue.id, projectId, {
									timeLeft: diff,
								});
							}

							if (diff < 200 && cue.hideOnMinus !== false) {
								cuesApi.updateCue(cue.id, projectId, {
									status: "going",
								});
							}
						}
					} else if (cue.timerType === "countup") {
						let diff = countup_diff(cue);

						if (cue.paused) {
							let paused =
								cue.pausedAtMS - cue.timestamp + (cue.startTimeMS || 0);

							setTN(paused);
						} else {
							setTN(diff);

							if (diff - cue.timeLeft > 1000) {
								cuesApi.updateCue(cue.id, projectId, {
									timeLeft: diff,
								});
							}

							if (diff > cue.durationMS - 120) {
								clearInterval(i);
								cuesApi.updateCue(cue.id, projectId, {
									status: "going",
								});
							}
						}
					} else if (cue.timerType === "time_of_day") {
						let now = moment().format("x");

						setTN(now);

					}
				}, 100)
			);
			// } else if (cue.status === "going") {
			// 	clearInterval(i);

			// 	setTimeout(() => {
			// 		return cuesApi.updateCue(cue.id, projectId, {
			// 			timeLeft: cue.timerType === "countdown" ? cue.durationMS : 0,
			// 		});
			// 	}, 1000);
		} else {
			clearInterval(i);
		}

		return () => {
			clearInterval(i);
		};
	}, [cue]);

	switch (cue.timerType) {
		case "time_of_day":
			return (
				<span>
					{moment(new Date())
						.add(cue.timezoneOffset || 0, "hour")
						.format(cue.format || "HH:mm:ss")}
					{/* <TimeSplit
						cue={cue}
						cloud={cloud}
						skip={skip}
						time={moment(new Date()).format(cue.format || "HH:mm:ss")}
					/> */}
				</span>
			);

		case "to_time_of_day":
			return (
				<span>
					{moment
						.duration(tN, "milliseconds")
						.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
					{/* <TimeSplit
					cue={cue}
					cloud={cloud}
					skip={skip}
					time={moment
						.duration(tN, "milliseconds")
						.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
				/> */}
				</span>
			);

		case "countdown":
			return (
				<span className={`${cue.paused && "animate-pulse"}`}>
					{moment
						.duration(tN, "milliseconds")
						.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
					{/* <TimeSplit
						cue={cue}
						cloud={cloud}
						skip={skip}
						time={moment
							.duration(tN, "milliseconds")
							.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
					/> */}
				</span>
			);

		case "countup":
			return (
				<span className={`${cue.paused && "animate-pulse"}`}>
					{moment
						.duration(tN, "milliseconds")
						.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
					{/* <TimeSplit
						cue={cue}
						cloud={cloud}
						skip={skip}
						time={moment
							.duration(tN, "milliseconds")
							.format(cue.format || "HH:mm:ss", { trim: cue.trim })}
					/> */}
				</span>
			);

		default:
			return null;
	}
};

const Time = ({ cue, cloud }) => {
	switch (cue.timerType) {
		case "time_of_day":
			return <TimeContents cue={cue} cloud={cloud} />;

		case "to_time_of_day":
			return <TimeContents cue={cue} cloud={cloud} />;

		case "countdown":
			return <TimeContents cue={cue} cloud={cloud} />;

		case "countup":
			return <TimeContents cue={cue} cloud={cloud} />;

		default:
			return null;
	}
};

const BigTime = ({ cue, cloud = false }) => {
	const { projectId } = useParams();
	const selectedProject = useSelector(
		(state) => state?.main?.projects?.[projectId]
	);
	const baseTheme = useSelector((state) =>
		cloud
			? state?.projects?.[projectId + "_themes"]?.[cue.theme]
			: cue.theme
			? state?.main?.projects?.[projectId]?.themes?.[cue.theme]
			: state?.main?.projects?.[projectId]?.themes?.[selectedProject?.theme]
	);
	const baseThemeId = cue.theme ? cue.theme : selectedProject?.theme;

	const [t, setT] = useState();

	const caclulateDifference = (min, max) => {
		if (cue.timerType === "to_time_of_day") {
			return (
				(cue.timeLeft / (cue.endTimestamp - cue.timestamp)) * (min - max) + max
			);
		}
		return (cue.timeLeft / stringToMS(cue.duration)) * (min - max) + max;
	};

	useEffect(() => {
		let total = -630;
		setT(total - (cue.timeLeft / stringToMS(cue.duration)) * total);
		// console.log(t);
	}, [cue]);

	return (
		<div className="flex justify-center items-center w-screen h-screen">
			<div className="flex justify-center item-center relative w-screen h-screen">
				<div
					className={`
				${baseThemeId}_${cue.type} ${cue.type}
				w-12/12 h-12/12 flex items-center justify-center`}
				>
					{cue.shape === "circle" && (
						<svg
							style={{
								width: "100vw",
								height: "100vh",
							}}
							className={`transform -rotate-90 transition-all duration-150`}
						>
							<circle
								opacity="10%"
								cx="48vw"
								cy="50vh"
								r="20vw"
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeWidth={`${baseTheme?.["big_time"]?.thickness || 3}vw`}
								fill="none"
								className={`${cue.type}_line_key `}
							/>
							<circle
								style={{
									transitionDuration: cue.status === "onair" ? "1s" : "0s",
									transitionTimingFunction: "linear",
								}}
								cx="48vw"
								cy="50vh"
								r="20vw"
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeWidth={`${baseTheme?.["big_time"]?.thickness || 3}vw`}
								strokeDasharray="130vw"
								strokeDashoffset={`${caclulateDifference(0, 130)}vw`}
								strokeLinecap={
									baseTheme?.["big_time"]?.roundedStyle === "butt"
										? "butt"
										: "round"
								}
								fill="none"
								className={`${cue.type}_line_key ${
									baseTheme?.["big_time"]?.elapsedStyle === "seconds"
										? "circle-timer-seconds"
										: ""
								}`}
							/>
						</svg>
					)}
					{cue.shape === "line" && (
						<svg
							style={{
								width: "100vw",
								height: "100vh",
							}}
							className="transform origin-center transition-transform duration-150"
						>
							<line
								opacity="10%"
								x1="35vw"
								x2="65vw"
								y1="66vh"
								y2="66vh"
								strokeWidth={`${baseTheme?.["big_time"]?.thickness || 2}vw`}
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeLinecap={
									baseTheme?.["big_time"]?.roundedStyle === "butt"
										? "butt"
										: "round"
								}
								className={`${cue.type}_line_key`}
							/>
							<line
								style={{
									transitionDuration: cue.status === "onair" ? "1s" : "0s",
									transitionTimingFunction: "linear",
								}}
								x1="35vw"
								x2="65vw"
								y1="66vh"
								y2="66vh"
								strokeWidth={`${baseTheme?.["big_time"]?.thickness || 2}vw`}
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeDasharray="30vw"
								strokeDashoffset={`${caclulateDifference(0, 30)}vw`}
								strokeLinecap={
									baseTheme?.["big_time"]?.roundedStyle === "butt"
										? "butt"
										: "round"
								}
								className={`${cue.type}_line_key ${
									baseTheme?.["big_time"]?.elapsedStyle === "seconds"
										? "line-timer-seconds"
										: ""
								}`}
							/>
						</svg>
					)}
					{cue.shape === "mask" && (
						<svg
							style={{
								width: "100vw",
								height: "100vh",
							}}
							className="transform origin-center transition-all duration-150"
						>
							<line
								opacity="10%"
								x1="33vw"
								x2="67vw"
								y1="53.5vh"
								y2="53.5vh"
								strokeWidth="12vw"
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeLinecap={
									baseTheme?.["big_time"]?.roundedStyle === "butt"
										? "butt"
										: "round"
								}
								className={`${cue.type}_line_key ${
									baseTheme?.["big_time"]?.elapsedStyle === "seconds"
										? "mask-timer-seconds"
										: ""
								}`}
							/>
							<line
								style={{
									transitionDuration: cue.status === "onair" ? "1s" : "0s",
									transitionTimingFunction: "linear",
								}}
								// opacity="50%"
								x1="33vw"
								x2="67vw"
								y1="53.5vh"
								y2="53.5vh"
								strokeWidth="12vw"
								stroke={checkForOverride(
									baseTheme,
									cue.preset,
									cue.type,
									"background",
									"colour"
								)}
								strokeDasharray="34vw"
								strokeDashoffset={`${caclulateDifference(0, 33.9)}vw`}
								strokeLinecap={
									baseTheme?.["big_time"]?.roundedStyle === "butt"
										? "butt"
										: "round"
								}
								className={`${cue.type}_line_key ${
									baseTheme?.["big_time"]?.elapsedStyle === "seconds"
										? "mask-timer-seconds"
										: ""
								}`}
							/>
						</svg>
					)}
				</div>

				<span
					style={{
						color: checkForOverride(
							baseTheme,
							cue.preset,
							cue.type,
							"h1",
							"colour"
						),
						fontSize: "10vw",
						fontFamily: checkForOverride(
							baseTheme,
							cue.preset,
							cue.type,
							"font",
							"family"
						),
					}}
					className={`${baseThemeId}_${cue.type}_large_text ${cue.type}_large_text ${cue.type}_text_key absolute text-gray-600 font-bold -translate-y-1/4  left-0 right-0 text-6xl text-center top-1/2 transform`}
				>
					<TimeContents cue={cue} skip={true} />
				</span>
				{cue.shape === "mask" && (
					<span
						style={{
							color: "#f1f1f1",
							clipPath: `inset(0vw ${caclulateDifference(33, 67)}vw 0 0)`,
							fontSize: "10vw",
							fontFamily: checkForOverride(
								baseTheme,
								cue.preset,
								cue.type,
								"font",
								"family"
							),
							mixBlendMode: "screen",
							transitionDuration: cue.status === "onair" ? "1s" : "0s",
							transitionTimingFunction: "linear",
						}}
						className={`absolute transition-tranform text-gray-600 font-bold -translate-y-1/4  left-0 right-0 text-6xl text-center top-1/2 transform
						`}
					>
						<TimeContents cue={cue} skip={true} />
					</span>
				)}
			</div>
		</div>
	);
};
